<template>
    <div class="DatatableLayout">
        <div class="DatatableLayout__header-bar">
            <span :class="classBuilder('DatatableLayout__header')">
                <template v-if="!isLoading">
                    <!-- @slot Header text -->
                    <slot name="headerText"><h3>{{ pageHeader }}</h3></slot>
                </template>
            </span>
            <div :class="classBuilder('DatatableLayout__header-actions')">
                <template v-if="status !== 'loading'">
                    <!-- @slot Header actions  -->
                    <slot name="headerActions"></slot>
                </template>
            </div>
        </div>
        <div class="DatatableLayout__table-controls">
            <div :class="classBuilder('DatatableLayout__table-filters')">
                <template v-if="status !== 'loading'">
                    <!-- @slot Table filters -->
                    <slot name="tableFilters"></slot>
                </template>
            </div>
            <small
                :class="classBuilder('DatatableLayout__table-info')"
            >
                <template v-if="showItems">
                    <!-- @slot Table Information (mostly used for pagination info-->
                    <slot name="tableInfo">
                        Showing {{ items.count }} of {{ items.total }}
                    </slot>
                </template>

            </small>
        </div>


        <div :class="`DatatableLayout__table-container${showSidebar ? '--compressed': '--full'}`">
            <!--
                Sets the sort field and sort direction
                @event set-sort
                @property object {sort: '', direction: ''}
            -->
            <!--
                v-model input
                @event input
            -->
            <!--
                Used as an alternative to input if a secondary behavior is necessary
                @event row-select
                @property object row data
            -->
            <RPDatatable
                :value="value"
                :hide-id="hideId"
                :headers="filteredHeaders"
                :table-data="filteredTableData"
                :sort="sort"
                :sort-direction="sortDirection"
                :status="tableStatus"
                :show-extra-options="showExtraOptions"
                :show-action-cell="showActionCell"
                table-type="page"
                @set-sort="s => $emit('set-sort', s)"
                @input="v => $emit('input', v)"
                @row-select="v => $emit('row-select', v)"
            >
                <template
                    v-for="header in filteredHeaders"
                    v-slot:[header.key]="{prop, rowData}"
                >
                    <!--
                        @slot Use header key as slot name to customize the display of data in a datacell
                        @binding {string} prop Property associated with this particular cell
                        @binding {object} rowData All data present in row
                    -->
                    <slot
                        :name="header.key"
                        :prop="prop"
                        :rowData="rowData"
                    >
                        {{ rowData[prop] }}
                    </slot>
                </template>
                <template v-slot:actionSlot="{ rowId }">
                    <!--
                        @slot used to display the actions in each row.
                        @binding {object} rowId rowId is included to trigger the proper action
                    -->
                    <slot
                        :rowId="rowId"
                        name="actionSlot"
                    >
                    </slot>
                </template>
                <template
                    v-if="showExtraOptions"
                    v-slot:extraOptions="{ rowId }"
                >
                    <slot
                        :rowId="rowId"
                        name="extraOptions"
                    ></slot>
                </template>
                <template v-slot:noResults>
                    <!--
                        @slot Content to display when the table data is empty
                    -->
                    <slot name="noResults">
                    </slot>
                </template>
            </RPDatatable>
        </div>
        <div
            v-if="showSidebar"
            :class="sidebarClass"
        >
            <template v-if="!isSidebarLoading">
                <div class="SideBar__header-bar">
                    <span class="SideBar__header">
                        <!-- @slot Sidebar header text -->
                        <slot name="sidebarHeaderText"><h4>{{ sidebarHeader }}</h4></slot>
                    </span>
                    <div class="SideBar__header-actions">
                        <!-- @slot Sidebar actions -->
                        <slot name="sidebarActions">
                        </slot>
                        <!--
                            Prompt to close sidebar which must be done from parent
                            @event close-sidebar
                        -->
                        <div
                            class="SideBar__close-icon"
                            @click="e => $emit('close-sidebar', e)"
                        >
                            <StreamlineIcon
                                :icon="closeIcon"
                                :size="16"
                                fill="#586672"
                            />
                        </div>
                    </div>
                </div>
                <div class="SideBar__body">
                    <!-- @slot primary sidebar content slot -->
                    <slot name="sidebar"></slot>
                </div>
            </template>
        </div>
        <div :class="`DatatableLayout__footer-bar${showSidebar ? '--compressed': '--full'}`">
            <!--
                Prompt to navigate to particular page in table data
                @event navToPage
                @property {object} page information
            -->
            <RPPagination
                :pagination="pagination"
                :show-total="showItems"
                @navToPage="p => $emit('navToPage', p)"
            />
        </div>
        <slot name="modal"></slot>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import RPDatatable from 'components/Complex/RPDatatable/RPDatatable.vue';
import RPPagination from 'components/Complex/Pagination/RPPagination';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import {Close as CloseIcon} from "@reviewpush/streamline-bold/lib/interface-essential/FormValidation";
import StreamlineIcon from "@reviewpush/streamline-icons-vue";

export default {
    name: 'RPDatatableLayout',
    components: {
        RPDatatable,
        RPPagination,
        StreamlineIcon
    },
    props: {
        /**
        * Shows or hides the sidebar
        */
        showSidebar: {
            type: Boolean,
            default: false,
        },
        /**
        * Sidebar header text
        */
        sidebarHeader: {
            type: String,
            default: ''
        },
        /**
        * Text displayed at top of page
        */
        pageHeader: {
            type: String,
            default: ''
        },
        /**
        * Information for the pagination component
        */
        pagination: {
            type: Object,
            default: () => ({
                pagination: {
                    totalPages: -1,
                    currentPage: -1
                }
            }),
        },
        /**
        * When displaying the sidebar the primary table will be compressed to show only the data from this field
        */
        compressedField: {
            type: String,
            default: ''
        },
        /**
        * Component Status for the entire Layout component (see status/index.js for list of statuses)
        */
        status: {
            type: String,
            default: stat.STANDBY,
        },
        /**
        * Component Status for the just the datatable component (see status/index.js for list of statuses)
        */
        tableStatus: {
            type: String,
            default: stat.STANDBY,
        },
        sidebarStatus: {
            type: String,
            default: stat.STANDBY,
        },
        /**
        * See Datatable
        */
        value: {
            type: [Array, String],
            required: true,
        },
        /**
        * See Datatable
        */
        hideId: {
            type: Boolean,
            default: false,
        },
        /**
        * See Datatable
        */
        headers: {
            type: Array,
            required: true,
        },
        /**
        * See Datatable
        */
        tableData: {
            type: Array,
            required: true,
        },
        /**
        * See Datatable
        */
        sort: {
            type: String,
            default: '',
        },
        /**
        * See Datatable
        */
        sortDirection: {
            type: String,
            default: 'asc',
            validator: val => ['asc', 'desc'].indexOf(val) !== -1,
        },
        /**
        * See Datatable
        */
        showExtraOptions: {
            type: Boolean,
            default: false,
        },
        /**
        * See Datatable
        */
        showActionCell: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        closeIcon(){
            return CloseIcon;
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        isSidebarLoading(){
            return this.sidebarStatus === stat.LOADING;
        },
        filteredHeaders(){
            return this.showSidebar
                ? cloneDeep(this.headers).filter(header => header.key === this.compressedField)
                    .map(h => {
                        delete h.sortable;
                        return h;
                    })
                : this.headers;
        },
        filteredTableData(){
            return this.showSidebar
                ? this.tableData.map(data => ({
                    id: data.id,
                    [this.compressedField]: data[this.compressedField]}))
                : this.tableData;
        },
        showPagination(){
            return !isEmpty(this.pagination) && this.pagination.totalPages > 1 && !this.isLoading;
        },
        showItems(){
            return !isEmpty(this.items) && this.items.count > 0;
        },
        sidebarClass(){
            return statusBuilder('SideBar', this.sidebarStatus);
        }
    },
    methods: {
        classBuilder(baseClass){
            return statusBuilder(baseClass, this.status);
        },
    }
};
</script>
<style lang="scss">
    .DatatableLayout {
        height: 100%;
        width: 100%;
        display: grid;
        grid-template-columns: 2fr 8fr;
        grid-template-rows: repeat(4, min-content);
        grid-column-gap: 1rem;
        &__header {
            @include loading(2rem, 8rem);
        }
        &__table-filters {
            @include loading(1.5rem, 26rem);
        }
        &__header-bar {
            grid-column: span 2;
            grid-row: 1;
            display:flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
            border-bottom: 1px solid $grey-100;
        }
        &__header-actions {
            @include loading(1.5rem, 12.5rem);
            min-width: 15rem;
            display: flex;
            align-items: center;
            & > * {
                margin-left: 1rem;
            }
        }
        &__table-controls {
            grid-column: span 2;
            grid-row: 2;
            display:flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
        }
        &__table-info {
            color: #586672;
            @include loading(1.5rem, 9rem);
        }
        &__table-container {
            &--full {
                margin-bottom: 3rem;
                grid-column: span 2;
                grid-row: 3;
            }
            &--compressed {
                margin-bottom: 3rem;
                grid-column: 1;
                grid-row: 3;
            }
        }
        &__footer-bar {
            &--full,
            &--compressed {
                margin-bottom: 5rem;
                grid-column: span 2;
                grid-row: 4;
            }
            &--compressed {
                justify-self: start;
            }
        }

        .SideBar {
            margin-bottom: 3rem;
            grid-column: 2;
            grid-row: 3;
            background: $white;
            border-radius: 4px;
            padding: 1.5rem;

            @include loading(100%, 100%);

            &__header-bar {
                display:flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 1rem;
            }
            &__header-actions {
                display: flex;
                align-items: center;

                @include loading;
            }
            &__close-icon{
                height: 1rem;
                width: 1rem;
                cursor: pointer;
                margin-left: 1rem;
            }
        }
    }
</style>

<docs>
    Datatable layout commonly used where the datatable is the primary ui element on the page.  Includes a sidebar element that can be opened and closed from the parent.
    ```[import](./examples/RPDatatableLayoutExample.vue)
    ```
</docs>
